/**
 * Drag-and-drop / click-to-upload zone. Accepts a single .docx file.
 *
 * No model selector or advanced options — those are server-internal. The
 * user picks a file; the upload happens immediately.
 *
 * Pass `progress` (a number between 0 and 1) while uploading to render a
 * thin progress bar. Pass `null`/`undefined` to omit it (the upload is
 * either not in flight or it's small enough that we don't track progress).
 */

import { useRef, useState, type DragEvent } from 'react';

export interface DropZoneProps {
  uploading: boolean;
  onFile: (file: File) => void;
  /** 0..1 fraction uploaded. Omit when not tracking. */
  progress?: number | null;
}

export function DropZone({ uploading, onFile, progress }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  }

  const pct = typeof progress === 'number' ? Math.max(0, Math.min(1, progress)) : null;

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      style={{
        border: `1px dashed ${over ? 'var(--ink)' : 'var(--border-strong)'}`,
        background: over ? 'var(--ink-tint)' : 'var(--vellum)',
        borderRadius: 8,
        padding: '32px 24px',
        textAlign: 'center',
        cursor: uploading ? 'wait' : 'pointer',
        opacity: uploading ? 0.85 : 1,
        transition: 'border-color var(--dur-base) var(--ease-out), background var(--dur-base) var(--ease-out)',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".docx"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--fg-1)', marginBottom: 6 }}>
        {uploading
          ? pct !== null
            ? `Uploading… ${Math.round(pct * 100)}%`
            : 'Uploading…'
          : 'Drop a .docx — or click to browse'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
        Up to 50 MB · auto-deleted after 7 days
      </div>
      {uploading && pct !== null && (
        <div
          aria-hidden
          style={{
            marginTop: 16,
            height: 4,
            background: 'var(--border)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct * 100}%`,
              height: '100%',
              background: 'var(--ink)',
              transition: 'width 120ms linear',
            }}
          />
        </div>
      )}
    </div>
  );
}
